// Customer Fulfillment Group Actions Configuration
// Actions available to customers for fulfillment groups

import { 
  ArrowPathIcon as ReturnIcon, 
  EyeIcon,
  XMarkIcon as XIcon,
  CheckIcon,
  TruckIcon,
  StarIcon,
  PencilIcon
} from '@heroicons/react/24/outline';

export const customerFulfillmentGroupActions = [
  // Cancel (processing)
  {
    id: 'cancel_fulfillment_group',
    label: 'Cancel',
    type: 'secondary',
    icon: XIcon,
    conditions: {
      userRole: 'customer',
      entityType: 'fulfillmentGroup',
      status: 'processing',
      timeConstraints: { within24Hours: true }
    },
    action: {
      type: 'modal',
      component: 'CancelGroupModal',
      mutation: 'useCancelFulfillmentGroupMutation',
      api: 'fulfillmentGroupApi'
    }
  },

  // Track Group Shipment (shipped_to_customer)
  {
    id: 'track_group_shipment',
    label: 'Track Shipment',
    type: 'secondary',
    icon: TruckIcon,
    conditions: {
      userRole: 'customer',
      entityType: 'fulfillmentGroup',
      status: 'shipped_to_customer'
    },
    action: {
      type: 'modal',
      component: 'TrackingModal',
      query: 'useGetTrackingQuery',
      api: 'fulfillmentGroupApi'
    }
  },

  // View Tracking (for multiple statuses with tracking available, but NOT shipped_to_customer since that has its own action)
  {
    id: 'view_tracking',
    label: 'View Tracking',
    type: 'secondary',
    icon: EyeIcon,
    conditions: {
      userRole: 'customer',
      entityType: 'fulfillmentGroup',
      status: ['delivery_completed_to_customer', 'return_in_transit_to_merchant'],
      hasTracking: true
    },
    action: {
      type: 'modal',
      component: 'TrackingModal',
      query: 'useGetTrackingQuery',
      api: 'fulfillmentGroupApi'
    }
  },

  // Delivery Completed (shipped_to_customer) - customer confirms receipt
  {
    id: 'confirm_delivery_completed',
    label: 'Delivery Completed',
    type: 'primary',
    icon: CheckIcon,
    conditions: {
      userRole: 'customer',
      entityType: 'fulfillmentGroup',
      status: 'shipped_to_customer'
    },
    action: {
      type: 'modal',
      component: 'ConfirmDeliveryModal',
      mutation: 'useConfirmFulfillmentGroupMutation',
      api: 'fulfillmentGroupApi'
    }
  },

  // Request Return (delivery_completed_to_customer)
  {
    id: 'request_group_return',
    label: 'Request Return',
    type: 'secondary',
    icon: ReturnIcon,
    conditions: {
      userRole: 'customer',
      entityType: 'fulfillmentGroup',
      status: 'delivery_completed_to_customer',
      allItemsDelivered: true,
      timeConstraints: { maxDaysAfterDelivery: 30 },
      noActiveGroupReturn: true
    },
    action: {
      type: 'redirect',
      target: '/dashboard/returns/request/{id}'
    }
  },

  // Cancel Return (return_requested) - unified for both partial and full returns
  {
    id: 'cancel_group_return',
    label: 'Cancel Return',
    type: 'secondary',
    icon: XIcon,
    conditions: {
      userRole: 'customer',
      entityType: 'fulfillmentGroup',
      status: ['return_requested','return_approved']
    },
    action: {
      type: 'modal',
      component: 'CancelGroupReturnModal'
    }
  },

  // Request Return (renting_in_progress) - for rental
  {
    id: 'request_group_return_rental',
    label: 'Request Return',
    type: 'secondary',
    icon: ReturnIcon,
    conditions: {
      userRole: 'customer',
      entityType: 'fulfillmentGroup',
      status: 'renting_in_progress',
      orderType: 'rental',
      noActiveGroupReturn: true
    },
    action: {
      type: 'modal',
      component: 'RequestGroupReturnModal'
    }
  },

  // Contact Support (return_rejected)
  {
    id: 'contact_support_return_rejected',
    label: 'Contact Support',
    type: 'warning',
    icon: PencilIcon,
    conditions: {
      userRole: 'customer',
      entityType: 'fulfillmentGroup',
      status: 'return_rejected'
    },
    action: {
      type: 'redirect',
      url: '/support'
    }
  },

  // Start Return (awaiting_return_shipment)
  {
    id: 'start_return',
    label: 'Start Return',
    type: 'primary',
    icon: ReturnIcon,
    conditions: {
      userRole: 'customer',
      entityType: 'fulfillmentGroup',
      status: ['awaiting_return_shipment','return_approved']
    },
    action: {
      type: 'redirect',
      target: '/dashboard/returns/ship/{id}'
    }
  },

  // Return Status (return_shipped without tracking or other return statuses)
  {
    id: 'return_status',
    label: 'Return Status',
    type: 'secondary',
    icon: TruckIcon,
    conditions: {
      userRole: 'customer',
      entityType: 'fulfillmentGroup',
      status: ['return_shipped', 'return_in_transit_to_merchant']
    },
    action: {
      type: 'redirect',
      target: '/dashboard/returns/ship/{id}'
    }
  },

  // Track Return (return_shipped with tracking)
  {
    id: 'track_return',
    label: 'Track Return',
    type: 'secondary',
    icon: EyeIcon,
    conditions: {
      userRole: 'customer',
      entityType: 'fulfillmentGroup',
      status: 'return_shipped',
      hasReturnTracking: true
    },
    action: {
      type: 'modal',
      component: 'TrackingModal',
      query: 'useGetFulfillmentGroupTrackingQuery',
      api: 'deliveryApi'
    }
  },

  // Review Products (post-delivery or during rental)
  {
    id: 'review_products',
    label: 'Review Products',
    type: 'secondary',
    icon: StarIcon,
    conditions: {
      userRole: 'customer',
      entityType: 'fulfillmentGroup',
      status: [
        'delivery_completed_to_customer',
        'pickup_completed_by_customer',
        'renting_in_progress',
        'return_requested',
        'return_approved',
        'awaiting_return_shipment',
        'return_shipped',
        'return_in_transit_to_merchant',
        'return_delivered',
        'return_completed'
      ]
    },
    action: {
      type: 'redirect',
      target: '/fulfillmentgroup/{id}/review-products'
    }
  }
]; 